import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, CaretDown, BookOpen, ChatCircleDots, ListChecks, CircleNotch } from "@phosphor-icons/react";
import { useContextStore } from "../../stores/context";
import { RubricChecklist } from "./RubricChecklist";
import { FeedbackPatterns } from "./FeedbackPatterns";
import { ContextSuggestions } from "./ContextSuggestions";
import { cn } from "../../lib/cn";

interface ContextPanelProps {
  className?: string;
}

type SectionId = "suggestions" | "rubric" | "feedback" | "materials";

function ContextPanel({ className }: ContextPanelProps) {
  const contextModel = useContextStore((s) => s.contextModel);
  const isBuilding = useContextStore((s) => s.isBuilding);
  const error = useContextStore((s) => s.error);
  const [openSections, setOpenSections] = useState<Set<SectionId>>(
    new Set(["suggestions", "rubric"]),
  );

  const toggleSection = (id: SectionId) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isBuilding) {
    return (
      <div className={cn("flex flex-col items-center justify-center gap-2 py-8", className)}>
        <CircleNotch size={16} weight="bold" className="animate-spin text-[var(--color-lever-peach)]" />
        <span className="text-[11px] text-[var(--text-muted)]">Building context model...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("px-3 py-4 text-[11px] text-[var(--color-soft-red)]", className)}>
        {error}
      </div>
    );
  }

  if (!contextModel) {
    return (
      <div className={cn("flex flex-col items-center gap-2 px-3 py-8 text-center", className)}>
        <Brain size={20} weight="duotone" className="text-[var(--text-muted)]" />
        <p className="text-[11px] text-[var(--text-muted)]">
          Context intelligence is available when working on assignments.
        </p>
      </div>
    );
  }

  const sections: { id: SectionId; label: string; icon: typeof ListChecks; count?: number }[] = [
    { id: "suggestions", label: "Suggestions", icon: Brain, count: useContextStore.getState().getVisibleSuggestions().length },
    { id: "rubric", label: "Rubric", icon: ListChecks, count: contextModel.rubric.length },
    { id: "feedback", label: "Past Feedback", icon: ChatCircleDots, count: contextModel.pastFeedback.length },
    { id: "materials", label: "Materials", icon: BookOpen, count: contextModel.materials.length },
  ];

  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      {/* Course header */}
      {contextModel.courseInfo && (
        <div className="px-3 py-2 border-b border-[var(--border-hairline)]">
          <p className="text-[11px] font-semibold text-[var(--text-primary)]">
            {contextModel.assignmentTitle}
          </p>
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
            {contextModel.courseInfo.name} · {contextModel.courseInfo.instructor}
          </p>
        </div>
      )}

      {sections.map(({ id, label, icon: Icon, count }) => {
        const isOpen = openSections.has(id);

        return (
          <div key={id}>
            <button
              onClick={() => toggleSection(id)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5",
                "text-[11px] font-medium text-[var(--text-secondary)]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors",
              )}
            >
              <Icon size={12} weight="bold" />
              <span className="flex-1 text-left">{label}</span>
              {count !== undefined && count > 0 && (
                <span className="text-[9px] font-mono text-[var(--text-muted)] bg-[var(--bg-surface-hover)] px-1.5 rounded-full">
                  {count}
                </span>
              )}
              <motion.span
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.15 }}
              >
                <CaretDown size={10} weight="bold" className="text-[var(--text-muted)]" />
              </motion.span>
            </button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-3 py-1.5">
                    {id === "suggestions" && <ContextSuggestions />}
                    {id === "rubric" && <RubricChecklist items={contextModel.rubric} />}
                    {id === "feedback" && <FeedbackPatterns patterns={contextModel.pastFeedback} />}
                    {id === "materials" && (
                      <div className="flex flex-col gap-1">
                        {contextModel.materials.map((m, i) => (
                          <div key={i} className="rounded-[var(--radius-sm)] bg-[var(--bg-surface)] border border-[var(--border-hairline)] px-2 py-1.5">
                            <p className="text-[10px] font-medium text-[var(--text-primary)]">{m.filename}</p>
                            <p className="text-[10px] text-[var(--text-muted)] mt-0.5 line-clamp-2">{m.summary}</p>
                          </div>
                        ))}
                        {contextModel.materials.length === 0 && (
                          <p className="text-[11px] text-[var(--text-muted)] italic">No materials uploaded.</p>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

ContextPanel.displayName = "ContextPanel";

export { ContextPanel };
export type { ContextPanelProps };
